import { useState, useEffect } from 'react'
import { useAtom } from 'jotai'
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  Avatar,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Menu,
  FormControlLabel,
  Checkbox,
  Stack,
} from '@mui/material'
import {
  Add as AddIcon,
  Search as SearchIcon,
  MoreVert as MoreVertIcon,
  Refresh as RefreshIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material'
import LoadingSpinner from '../components/LoadingSpinner'
import PageHelpButton from '../components/PageHelpButton'
import { colors } from '../theme'
import { usersApi, User, UserRole, UserStatus, CreateUserInput, ApiError } from '../services/api'
import { userAtom } from '../state/auth'
import { useNotification } from '../hooks/useNotification'
import { isAdminOrManager } from '../utils/roles'

const roleLabels: Record<UserRole, string> = {
  admin: 'Admin',
  sales: 'Sales',
  client_success: 'Client Success',
  resume_writer: 'Resume Writer',
  coach: 'Coach',
  client: 'Client',
}

const roleColors: Record<UserRole, string> = {
  admin: '#ef4444',
  sales: '#f59e0b',
  client_success: '#10b981',
  resume_writer: colors.primary,
  coach: '#8b5cf6',
  client: '#64748b',
}

const statusColors: Record<UserStatus, string> = {
  active: '#10b981',
  inactive: '#9e9e9e',
  suspended: '#f59e0b',
  deleted: '#ef4444',
}

const ROLES: UserRole[] = ['admin', 'sales', 'client_success', 'resume_writer', 'coach']

export default function Users() {
  const [currentUser] = useAtom(userAtom)
  const [searchQuery, setSearchQuery] = useState('')
  const [users, setUsers] = useState<User[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const { showSuccess, showError } = useNotification()

  // Add user dialog
  const [addDialogOpen, setAddDialogOpen] = useState(false)
  const [newUser, setNewUser] = useState<CreateUserInput>({
    email: '',
    name: '',
    role: 'sales',
    isManager: false,
  })
  const [addLoading, setAddLoading] = useState(false)
  const [addUserErrors, setAddUserErrors] = useState<Partial<Record<string, string>>>({})

  // Edit user dialog
  const [editDialogOpen, setEditDialogOpen] = useState(false)
  const [editingUser, setEditingUser] = useState<User | null>(null)
  const [editLoading, setEditLoading] = useState(false)

  // Menu state
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null)
  const [menuUser, setMenuUser] = useState<User | null>(null)

  const isAdmin = isAdminOrManager(currentUser)
  // isManager merged into isAdmin via isAdminOrManager helper
  const canManageUsers = isAdmin
  // MM-9: any staff member can onboard a teammate. Editing, deleting and role
  // changes stay with admins/managers, so the roster is read-only for staff.
  const canInviteUsers = currentUser ? currentUser.role !== 'client' : false

  const loadUsers = async () => {
    try {
      setLoading(true)
      setError(null)
      const result = await usersApi.list()
      // Ensure we always have an array
      setUsers(Array.isArray(result) ? result : [])
    } catch (err) {
      console.error('Error loading users:', err)
      setError(err instanceof Error ? err.message : 'Failed to load users')
      setUsers([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadUsers()
  }, [])

  const filteredUsers = users.filter(
    (user) =>
      user.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchQuery.toLowerCase())
  )

  const handleAddUser = async () => {
    if (!newUser.email || !newUser.name) {
      showError('Email and name are required')
      return
    }

    try {
      setAddLoading(true)
      setError(null)
      setAddUserErrors({})
      await usersApi.create(newUser)
      showSuccess('User created successfully')
      setAddDialogOpen(false)
      setNewUser({ email: '', name: '', role: 'sales', isManager: false })
      loadUsers()
    } catch (err) {
      console.error('Error creating user:', err)
      if (err instanceof ApiError && err.status === 409 && err.field) {
        setAddUserErrors({ [err.field]: err.message })
      } else {
        showError(err instanceof Error ? err.message : 'Failed to create user')
      }
    } finally {
      setAddLoading(false)
    }
  }

  const handleEditUser = async () => {
    if (!editingUser) return

    try {
      setEditLoading(true)
      setError(null)
      await usersApi.update({
        email: editingUser.email,
        name: editingUser.name,
        role: editingUser.role,
        status: editingUser.status,
        phone: editingUser.phone,
        isManager: editingUser.isManager,
      })
      showSuccess('User updated successfully')
      setEditDialogOpen(false)
      setEditingUser(null)
      loadUsers()
    } catch (err) {
      console.error('Error updating user:', err)
      showError(err instanceof Error ? err.message : 'Failed to update user')
    } finally {
      setEditLoading(false)
    }
  }

  const handleDeleteUser = async (user: User) => {
    if (!window.confirm(`This will permanently delete ${user.name}. It cannot be undone.`)) return

    try {
      setError(null)
      await usersApi.delete(user.email)
      showSuccess('User deleted successfully')
      loadUsers()
    } catch (err) {
      console.error('Error deleting user:', err)
      showError(err instanceof Error ? err.message : 'Failed to delete user')
    }
    handleCloseMenu()
  }

  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>, user: User) => {
    setMenuAnchor(event.currentTarget)
    setMenuUser(user)
  }

  const handleCloseMenu = () => {
    setMenuAnchor(null)
    setMenuUser(null)
  }

  const handleOpenEdit = () => {
    if (menuUser) {
      setEditingUser({ ...menuUser })
      setEditDialogOpen(true)
    }
    handleCloseMenu()
  }

  if (!canInviteUsers) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <Typography variant="h5" color="text.secondary">
          You don't have permission to manage users
        </Typography>
      </Box>
    )
  }

  if (loading) {
    return <LoadingSpinner />
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 1 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography variant="h4" sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>Users</Typography>
          <PageHelpButton articleId="admin-invite-user" />
        </Stack>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={loadUsers}
            disabled={loading}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setAddDialogOpen(true)}
          >
            Add User
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Card>
        <CardContent>
          <Box sx={{ mb: 3 }}>
            <TextField
              placeholder="Search users..."
              size="small"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
              sx={{ width: { xs: '100%', sm: 300 } }}
            />
          </Box>

          {filteredUsers.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography color="text.secondary">
                {users.length === 0 ? 'No users yet' : 'No users match your search'}
              </Typography>
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>User</TableCell>
                    <TableCell>Role</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Created</TableCell>
                    {canManageUsers && <TableCell align="right">Actions</TableCell>}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredUsers.map((user) => (
                    <TableRow key={user.email} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Avatar sx={{ bgcolor: roleColors[user.role], color: 'white' }}>
                            {user.name?.[0]?.toUpperCase() || '?'}
                          </Avatar>
                          <Box>
                            <Typography variant="subtitle2">{user.name}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {user.email}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                          <Chip
                            label={roleLabels[user.role]}
                            size="small"
                            sx={{
                              backgroundColor: `${roleColors[user.role]}20`,
                              color: roleColors[user.role],
                              fontWeight: 500,
                            }}
                          />
                          {user.isManager && (
                            <Chip
                              label="Manager"
                              size="small"
                              sx={{
                                backgroundColor: '#8b5cf620',
                                color: '#8b5cf6',
                                fontWeight: 500,
                              }}
                            />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={user.status}
                          size="small"
                          sx={{
                            backgroundColor: `${statusColors[user.status]}20`,
                            color: statusColors[user.status],
                            fontWeight: 500,
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {new Date(user.createdAt).toLocaleDateString()}
                        </Typography>
                      </TableCell>
                      {canManageUsers && (
                        <TableCell align="right">
                          <IconButton
                            size="small"
                            aria-label={`Actions for ${user.name}`}
                            onClick={(e) => handleOpenMenu(e, user)}
                            disabled={user.email === currentUser?.email}
                          >
                            <MoreVertIcon />
                          </IconButton>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Action Menu */}
      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={handleCloseMenu}>
        <MenuItem onClick={handleOpenEdit}>
          <EditIcon sx={{ mr: 1, fontSize: 18 }} />
          Edit
        </MenuItem>
        <MenuItem
          onClick={() => menuUser && handleDeleteUser(menuUser)}
          sx={{ color: 'error.main' }}
        >
          <DeleteIcon sx={{ mr: 1, fontSize: 18 }} />
          Delete
        </MenuItem>
      </Menu>

      {/* Add User Dialog */}
      <Dialog open={addDialogOpen} onClose={() => { setAddDialogOpen(false); setAddUserErrors({}) }} maxWidth="sm" fullWidth>
        <DialogTitle>Add New User</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Name"
              value={newUser.name}
              onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
              fullWidth
              required
            />
            <TextField
              label="Email"
              type="email"
              value={newUser.email}
              onChange={(e) => {
                setNewUser({ ...newUser, email: e.target.value })
                if (addUserErrors.email) setAddUserErrors((prev) => ({ ...prev, email: undefined }))
              }}
              error={!!addUserErrors.email}
              helperText={addUserErrors.email}
              fullWidth
              required
            />
            <TextField
              label="Phone"
              value={newUser.phone || ''}
              onChange={(e) => {
                setNewUser({ ...newUser, phone: e.target.value })
                if (addUserErrors.phone) setAddUserErrors((prev) => ({ ...prev, phone: undefined }))
              }}
              error={!!addUserErrors.phone}
              helperText={addUserErrors.phone}
              fullWidth
            />
            <FormControl fullWidth>
              <InputLabel>Role</InputLabel>
              <Select
                value={newUser.role}
                label="Role"
                onChange={(e) => setNewUser({ ...newUser, role: e.target.value as UserRole })}
              >
                {ROLES.filter((role) => isAdmin || role !== 'admin').map((role) => (
                  <MenuItem key={role} value={role}>
                    {roleLabels[role]}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {isAdmin && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={newUser.isManager || false}
                    onChange={(e) => setNewUser({ ...newUser, isManager: e.target.checked })}
                  />
                }
                label="Grant Manager Permissions (admin-level access + stage gate notifications)"
              />
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleAddUser} variant="contained" disabled={addLoading}>
            {addLoading ? <CircularProgress size={20} /> : 'Add User'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit User</DialogTitle>
        <DialogContent>
          {editingUser && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <TextField label="Email" value={editingUser.email} fullWidth disabled />
              <TextField
                label="Name"
                value={editingUser.name}
                onChange={(e) => setEditingUser({ ...editingUser, name: e.target.value })}
                fullWidth
                required
              />
              <TextField
                label="Phone"
                value={editingUser.phone || ''}
                onChange={(e) => setEditingUser({ ...editingUser, phone: e.target.value })}
                fullWidth
              />
              <FormControl fullWidth>
                <InputLabel>Role</InputLabel>
                <Select
                  value={editingUser.role}
                  label="Role"
                  onChange={(e) =>
                    setEditingUser({ ...editingUser, role: e.target.value as UserRole })
                  }
                  disabled={!isAdmin && editingUser.role === 'admin'}
                >
                  {ROLES.filter((role) => isAdmin || role !== 'admin').map((role) => (
                    <MenuItem key={role} value={role}>
                      {roleLabels[role]}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={editingUser.status}
                  label="Status"
                  onChange={(e) =>
                    setEditingUser({ ...editingUser, status: e.target.value as UserStatus })
                  }
                >
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                  <MenuItem value="suspended">Suspended</MenuItem>
                </Select>
              </FormControl>
              {isAdmin && (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={editingUser.isManager || false}
                      onChange={(e) =>
                        setEditingUser({ ...editingUser, isManager: e.target.checked })
                      }
                    />
                  }
                  label="Grant Manager Permissions (admin-level access + stage gate notifications)"
                />
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleEditUser} variant="contained" disabled={editLoading}>
            {editLoading ? <CircularProgress size={20} /> : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
